import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { transactionsAPI, usersAPI, feesAPI, formatApiError } from '../services/api';
import { toast } from 'sonner';
import PageShell from '../components/PageShell';
import { PillInput, PillButton } from '../components/PillKit';
import { CheckCircle, MagnifyingGlass } from '@phosphor-icons/react';

export default function SendMoneyPage() {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [feeInfo, setFeeInfo] = useState(null);

  const calculateFee = useCallback(async (amt) => {
    const parsed = parseFloat(amt);
    if (isNaN(parsed) || parsed <= 0) { setFeeInfo(null); return; }
    try { const res = await feesAPI.calculate(parsed); setFeeInfo(res.data); } catch (err) { console.error('Fee calculation failed:', err); setFeeInfo(null); }
  }, []);

  useEffect(() => { const t = setTimeout(() => calculateFee(amount), 300); return () => clearTimeout(t); }, [amount, calculateFee]);

  const handleSearch = async (q) => {
    setEmail(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try { const r = await usersAPI.search(q); setSearchResults(r.data.filter((u) => u.user_id !== user?.user_id)); } catch (err) { console.error('User search failed:', err); }
  };

  const selectUser = (u) => { setSelectedUser(u); setEmail(u.email); setSearchResults([]); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) return toast.error('Enter a valid amount');
    if (!email) return toast.error('Enter a recipient');
    setLoading(true);
    try {
      await transactionsAPI.send(email, parsed, note);
      setSuccess(true);
      await refreshUser();
      toast.success('Money sent!');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <PageShell testId="send-success" back title={{ line1: 'Money Sent!', line2: '' }}>
        <div className="flex flex-col items-center gap-6 mt-8">
          <CheckCircle className="w-20 h-20 text-[#22C55E]" weight="fill" />
          <p className="text-white/80 text-center">${parseFloat(amount).toFixed(2)} sent to {email}</p>
          <div className="space-y-3 w-full max-w-sm">
            <PillButton onClick={() => { setSuccess(false); setEmail(''); setAmount(''); setNote(''); setSelectedUser(null); setFeeInfo(null); }} data-testid="send-another-btn">
              Send Another
            </PillButton>
            <Link to="/dashboard"><PillButton variant="white">Back to Dashboard</PillButton></Link>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell testId="send-money-page" back title={{ line1: 'Send Money', line2: 'Pay anyone, instantly' }}>
      <form onSubmit={handleSubmit} className="space-y-5 max-w-md">
        {/* Recipient */}
        <div className="relative">
          <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block">Recipient</label>
          <div className="relative">
            <MagnifyingGlass className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
            <PillInput
              type="text"
              placeholder="Name, email, or $username"
              value={email}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-14"
              data-testid="recipient-input"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="absolute z-20 w-full bg-black border-[3px] border-white rounded-[18px] mt-2 max-h-64 overflow-auto">
              {searchResults.map((u) => (
                <button key={u.user_id} type="button" onClick={() => selectUser(u)} className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-white/5 border-b border-white/10 last:border-0" data-testid={`user-result-${u.user_id}`}>
                  <div className="w-9 h-9 bg-[#22C55E]/15 rounded-full flex items-center justify-center text-[#22C55E] font-bold">{u.name?.[0]?.toUpperCase() || 'U'}</div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-bold truncate">{u.name}</p>
                    <p className="text-white/50 text-xs truncate">{u.email}{u.username ? ` · $${u.username}` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {selectedUser && (
            <div className="mt-3 bg-black border-[3px] border-[#22C55E] rounded-full px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 bg-[#22C55E]/20 rounded-full flex items-center justify-center text-[#22C55E] font-bold">{selectedUser.name?.[0]?.toUpperCase() || 'U'}</div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">{selectedUser.name}</p>
                <p className="text-white/50 text-xs truncate">{selectedUser.email}</p>
              </div>
              <button type="button" onClick={() => { setSelectedUser(null); setEmail(''); }} className="text-white/40 text-xl px-2" aria-label="Clear">×</button>
            </div>
          )}
        </div>

        {/* Amount */}
        <div>
          <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block">Amount</label>
          <div className="relative">
            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-2xl font-bold text-white/60 z-10">$</span>
            <PillInput
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="pl-12 text-2xl font-extrabold"
              data-testid="amount-input"
            />
          </div>
          <p className="text-xs text-white/40 mt-2">Balance: ${user?.wallet_balance?.toFixed(2) || '0.00'}</p>
        </div>

        {feeInfo && feeInfo.fee > 0 && (
          <div className="bg-black border-[3px] border-white/40 rounded-[18px] px-4 py-3 text-sm" data-testid="fee-breakdown">
            <div className="flex justify-between text-white/70"><span>Amount</span><span className="font-bold text-white">${parseFloat(amount).toFixed(2)}</span></div>
            <div className="flex justify-between text-white/70 mt-1"><span>Fee ({feeInfo.percentage_fee}% + ${feeInfo.flat_fee?.toFixed(2)})</span><span className="font-bold text-white">${feeInfo.fee.toFixed(2)}</span></div>
            <div className="border-t border-white/10 mt-2 pt-2 flex justify-between font-bold"><span className="text-white">You pay</span><span className="text-[#22C55E]">${feeInfo.sender_pays?.toFixed(2)}</span></div>
          </div>
        )}

        {/* Note */}
        <div>
          <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block">Note (Optional)</label>
          <PillInput
            type="text"
            placeholder="What's this for?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="note-input"
          />
        </div>

        <PillButton onClick={handleSubmit} disabled={loading || !email || !amount} data-testid="send-button">
          {loading ? 'Sending…' : feeInfo?.fee > 0 ? `Send $${parseFloat(amount || 0).toFixed(2)} (+ $${feeInfo.fee.toFixed(2)} fee)` : `Send $${parseFloat(amount || 0).toFixed(2)}`}
        </PillButton>
      </form>
    </PageShell>
  );
}
